import React, { FC, useEffect, useState } from 'react';
import { OTPInputV2 } from 'swui';
import OTPTimer from './OTPTimer';
import ResendPasscodeModal from './ResendPasscodeModal';
import BotDetectionModal from './BotDetectionModal';
import { InputStyle, InputWrapperStyle, OTPInputWrapper, Title } from './OTPInputStyle';
import colors, { primaryBG } from '../../theme/colors';
import { isDarkMode, isEmpty } from '../../utils/helpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const OTPInput: FC<any> = ({
  key,
  emailVerifyData,
  otpStatus,
  resendOTPTitle,
  setOTPValue,
  customInputStyle,
  handleOTPAutoSubmit,
  type,
  handleBotButtonClick,
  handleOnBotModalClose,
  redirectStep,
  onResendClick,
  resetPhoneOtp,
  setResetPhoneOtp,
}) => {
  const [resetOtp, setResetOtp] = useState(false);
  const [focus, setFocus] = useState(false);
  const [showResendPasscodeModal, setShowResendPasscodeModal] = useState<boolean>(false);
  const [showMaxAttamptModal, setShowMaxAttamptModal] = useState<boolean>(false);
  const { isFetching, isOtpSent } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    setFocus(true);
  }, []);

  useEffect(() => {
    isOtpSent && setResetOtp(false);
    isOtpSent && setResetPhoneOtp !== undefined && setResetPhoneOtp(false);
  });

  useEffect(() => {
    if (emailVerifyData?.maxAttamptReached && ['login', 'join'].includes(emailVerifyData?.type)) {
      setShowMaxAttamptModal(true);
    }
  }, [emailVerifyData?.maxAttamptReached, emailVerifyData?.type]);

  const handleResendPasscodeClick = () => {
    setOTPValue('');

    // Use custom handler if provided
    if (onResendClick) {
      onResendClick();
    } else {
      setShowResendPasscodeModal(true);
    }
  };

  const getInputColor = () => {
    if (!['login', 'join'].includes(type)) {
      return {
        backgroundColor: isDarkMode() ? '#113956' : primaryBG,
        color: isDarkMode() ? '#FFFFFF' : '#333333',
        border: isDarkMode() ? '1px solid #F2F2F2' : '1px solid #DAE1E9',
      };
    } else {
      return { backgroundColor: primaryBG, border: '1px solid #DAE1E9' };
    }
  };

  const isOTPError = otpStatus === 'error' && !isFetching && !resetOtp;

  return (
    <>
      <OTPInputWrapper>
        {isOTPError ? (
          <Title style={{ color: colors.light.warning.primary }}>Passcode does not match</Title>
        ) : otpStatus === 'success' ? (
          <Title style={{ color: '#24A83A' }}>Success!</Title>
        ) : (
          <Title>Please enter your passcode</Title>
        )}
        <OTPInputV2
          length={6}
          key={key}
          onChangeOTP={value => {
            setOTPValue(value);
          }}
          type="number"
          autoFocus={focus}
          hasError={isOTPError}
          onResetOTP={resetPhoneOtp !== undefined ? resetPhoneOtp : resetOtp}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              return false;
            }
          }}
          onPaste={e => {
            e.preventDefault();
            if (!emailVerifyData?.is_last_attempt) {
              const clipboardOTP = e.clipboardData.getData('text/plain').trim();

              if (!isEmpty(clipboardOTP)) {
                setOTPValue(clipboardOTP);
                handleOTPAutoSubmit(clipboardOTP);
              }
            }
          }}
          style={InputWrapperStyle}
          inputStyle={{
            ...InputStyle,
            ...customInputStyle,
            ...getInputColor(),
          }}
          inputClassName={`focus-otp-input ${isOTPError ? 'otp-error' : ''}`}
        />
        <OTPTimer
          resendOTPTitle={resendOTPTitle}
          resendPasscodeClick={handleResendPasscodeClick}
          isLastAttempt={
            (['login', 'join'].includes(emailVerifyData?.type) && emailVerifyData?.is_last_attempt) ||
            (type == 'phone' && emailVerifyData?.maxAttamptReached)
          }
          type={type}
          onResetOTP={resetPhoneOtp !== undefined ? resetPhoneOtp : resetOtp}
        />
      </OTPInputWrapper>
      <ResendPasscodeModal
        showResendPasscodeModal={showResendPasscodeModal}
        handleClose={() => setShowResendPasscodeModal(false)}
        type={type}
        emailVerifyData={emailVerifyData}
        redirectStep={redirectStep}
        setResetOtp={setResetOtp}
      />
      <BotDetectionModal
        showBotModal={showMaxAttamptModal}
        handleClose={() => {
          handleOnBotModalClose && handleOnBotModalClose();
          setShowMaxAttamptModal(false);
        }}
        handleClick={(): void => {
          handleBotButtonClick();
          setShowMaxAttamptModal(false);
          setResetOtp(true);
        }}
        type={type}
        email={emailVerifyData?.email}
        phoneNumber={emailVerifyData?.phoneNumber}
      />
    </>
  );
};

export default OTPInput;
